/**
 * Stock Report Totals Summary Component
 * @see US-16.5 - UI Rapport Principal
 */

import { Card } from '@/components/ui';
import { formatCentimes } from '@/lib/format';
import { useCurrency } from '@/hooks';
import type { StockReportTotals } from '@bstock/shared';

interface StockReportTotalsSummaryProps {
  totals: StockReportTotals;
}

export function StockReportTotalsSummary({ totals }: StockReportTotalsSummaryProps) {
  const currency = useCurrency();

  const items = [
    {
      label: 'Stock Initial',
      value: totals.stockInitialValueCentimes,
      color: 'text-neutral-700',
    },
    {
      label: 'Achats',
      value: totals.receiptsValueCentimes,
      color: 'text-success-600',
      prefix: '+',
    },
    {
      label: 'Transfert In',
      value: totals.transfersInValueCentimes,
      color: 'text-success-600',
      prefix: '+',
    },
    {
      label: 'Transfert Out',
      value: totals.transfersOutValueCentimes,
      color: 'text-danger-600',
    },
    {
      label: 'Stock Final',
      value: totals.stockFinalValueCentimes,
      color: 'text-primary-600 font-semibold',
    },
    null, // Separator
    {
      label: 'Ventes',
      value: totals.salesValueCentimes,
      color: 'text-neutral-700',
    },
    {
      label: 'Pertes Brut',
      value: totals.lossesRawValueCentimes,
      color: 'text-danger-600',
    },
    {
      label: 'Pertes Finis',
      value: totals.lossesFinishedValueCentimes,
      color: 'text-danger-600',
    },
    {
      label: 'Pertes Excep.',
      value: totals.lossesExceptionalValueCentimes,
      color: 'text-warning-600',
    },
    {
      label: 'Repas Employes',
      value: totals.employeeMealsValueCentimes,
      color: 'text-neutral-600',
    },
    null, // Separator
    {
      label: 'Conso. Theorique',
      value: totals.consumptionTheoreticalValueCentimes,
      color: 'text-neutral-700',
    },
    {
      label: 'Conso. Reelle',
      value: totals.consumptionRealValueCentimes,
      color: 'text-neutral-700',
    },
    {
      label: 'Ecart Total',
      value: totals.varianceValueCentimes,
      color: totals.varianceValueCentimes > 0 ? 'text-danger-600 font-semibold' : 'text-success-600 font-semibold',
      prefix: totals.varianceValueCentimes > 0 ? '+' : '',
    },
  ];

  return (
    <Card className="p-4">
      <h3 className="text-sm font-medium text-neutral-500 mb-3">Resume des valorisations</h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-x-4 gap-y-3 sm:gap-4">
        {items.map((item, index) =>
          item === null ? (
            <div key={`sep-${index}`} className="hidden lg:block" />
          ) : (
            <div key={item.label} className="flex flex-col min-w-0">
              <span className="text-xs text-neutral-500 mb-0.5">{item.label}</span>
              <span className={`text-sm font-medium ${item.color} whitespace-nowrap`}>
                {item.prefix ?? ''}{formatCentimes(item.value, currency)}
              </span>
            </div>
          )
        )}
      </div>
    </Card>
  );
}
